import React, { useState, useEffect } from "react";

import {
  Axios,
  Loading,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  toast,
  ApiUrl,
  withRouter,
} from "../../../../../../../components";

const { VENUE_PAYMENTS_API } = ApiUrl;

const VenuePayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    venue_payment: "",
    stenography: "",
    transcription: "",
    pantry_service: "",
    video_conference: "",
  });
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [apiErrors, setApiErrors] = useState({});
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(VENUE_PAYMENTS_API);
      const { status, message, data } = response.data;
      if (status) {
        setPayments(data);
      } else {
        toast.error(message || "Failed to fetch venue payments");
      }
    } catch (error) {
      console.error("Error fetching venue payments:", error);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Error fetching venue payments"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (apiErrors[name]) {
      setApiErrors((prev) => {
        const newApiErrors = { ...prev };
        delete newApiErrors[name];
        return newApiErrors;
      });
    }

    if (value && isNaN(value)) {
      setErrors((prev) => ({
        ...prev,
        [name]: "Please enter a valid number",
      }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const fields = [
      "venue_payment",
      "stenography",
      "transcription",
      "pantry_service",
      "video_conference",
    ];

    fields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
      } else if (isNaN(formData[field])) {
        newErrors[field] = "Please enter a valid number";
      }
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the form errors");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setApiErrors({});
      let response;
      let successMessage;

      if (editId) {
        response = await Axios.post(`${VENUE_PAYMENTS_API}/${editId}`, formData);
        successMessage = "Venue payment updated successfully";
      } else {
        response = await Axios.post(VENUE_PAYMENTS_API, formData);
        successMessage = "Venue payment created successfully";
      }

      const { status, message } = response.data;
      if (status) {
        toast.success(message || successMessage);
        resetForm();
        fetchPayments();
        setOpenDialog(false);
      } else {
        if (response.data.errors) {
          setApiErrors(response.data.errors);
          toast.error("Please fix the form errors");
        } else {
          toast.error(message || "Operation failed");
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      if (error.response?.data?.errors) {
        setApiErrors(error.response.data.errors);
        toast.error("Please fix the form errors");
      } else {
        toast.error(
          error.response?.data?.message ||
          error.message ||
          "An error occurred"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (payment) => {
    setFormData({
      venue_payment: payment.venue_payment,
      stenography: payment.stenography,
      transcription: payment.transcription,
      pantry_service: payment.pantry_service,
      video_conference: payment.video_conference,
    });
    setEditId(payment.id);
    setErrors({});
    setApiErrors({});
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this payment record?")) {
      try {
        setLoading(true);
        const response = await Axios.post(`${VENUE_PAYMENTS_API}/${id}/delete`);
        const { status, message } = response.data;
        if (status) {
          toast.success(message || "Payment record deleted successfully");
          fetchPayments();
        } else {
          toast.error(message || "Failed to delete payment record");
        }
      } catch (error) {
        console.error("Error deleting payment:", error);
        toast.error(
          error.response?.data?.message ||
          error.message ||
          "Error deleting payment record"
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      venue_payment: "",
      stenography: "",
      transcription: "",
      pantry_service: "",
      video_conference: "",
    });
    setEditId(null);
    setErrors({});
    setApiErrors({});
  };

  const getFieldError = (fieldName) => {
    return errors[fieldName] || apiErrors[fieldName]?.[0];
  };

  const calculateFormTotal = () => {
    return (
      Number(formData.venue_payment || 0) +
      Number(formData.stenography || 0) +
      Number(formData.transcription || 0) +
      Number(formData.pantry_service || 0) +
      Number(formData.video_conference || 0)
    );
  };

  const calculateOverallTotal = () => {
    return payments.reduce((sum, payment) => {
      return (
        sum +
        Number(payment.venue_payment || 0) +
        Number(payment.stenography || 0) +
        Number(payment.transcription || 0) +
        Number(payment.pantry_service || 0) +
        Number(payment.video_conference || 0)
      );
    }, 0);
  };

  return (

    <div style={{ padding: "20px", backgroundColor: "#fff", marginTop: "20px", marginRight: "22px" }}>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Loading />
        </div>
      )}

      <Grid container spacing={2} style={{ marginBottom: "20px", alignItems: "center" }}>
        <Grid item xs={6}>
          <h2 style={{ margin: 0 }}>Venue Booking Payments Management</h2>
        </Grid>
        {payments.length === 0 && (
          <Grid item xs={6} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              style={{
                backgroundColor: "#083266",
                color: "#fff",
              }}
              onClick={() => {
                resetForm();
                setOpenDialog(true);
              }}
            >
              Add Payment
            </Button>
          </Grid>
        )}
      </Grid>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? "Edit Payment" : "Add New Payment"}</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: "10px", fontWeight: "bold", fontSize: "16px" }}>
            Total Amount: ₹{calculateFormTotal()}
          </div>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} style={{ marginTop: "10px" }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Venue Payment"
                  name="venue_payment"
                  value={formData.venue_payment}
                  onChange={handleChange}
                  error={!!getFieldError("venue_payment")}
                  helperText={getFieldError("venue_payment")}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Stenography"
                  name="stenography"
                  value={formData.stenography}
                  onChange={handleChange}
                  error={!!getFieldError("stenography")}
                  helperText={getFieldError("stenography")}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Transcription"
                  name="transcription"
                  value={formData.transcription}
                  onChange={handleChange}
                  error={!!getFieldError("transcription")}
                  helperText={getFieldError("transcription")}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pantry Service"
                  name="pantry_service"
                  value={formData.pantry_service}
                  onChange={handleChange}
                  error={!!getFieldError("pantry_service")}
                  helperText={getFieldError("pantry_service")}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Video Conference"
                  name="video_conference"
                  value={formData.video_conference}
                  onChange={handleChange}
                  error={!!getFieldError("video_conference")}
                  helperText={getFieldError("video_conference")}
                  required
                />
              </Grid>
            </Grid>
          </form>

        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              resetForm();
              setOpenDialog(false);
            }}
            disabled={loading}
            style={{ backgroundColor: "#083266", color: "#fff" }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit}
            disabled={loading}
            style={{ backgroundColor: "#083266", color: "#fff" }}
          >
            {editId ? "Update" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper style={{ padding: "16px", marginTop: "20px" }}>
        <div style={{ marginBottom: "10px", fontWeight: "bold", fontSize: "18px" }}>
          Total Amount: ₹{calculateOverallTotal()}
        </div>
        {payments.length > 0 ? (
          <Table>
            <TableBody>
              <TableRow>
                <TableCell><strong>Venue Payment</strong></TableCell>
                <TableCell><strong>Stenography</strong></TableCell>
                <TableCell><strong>Transcription</strong></TableCell>
                <TableCell><strong>Pantry Service</strong></TableCell>
                <TableCell><strong>Video Conference</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
              {payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{payment.venue_payment}</TableCell>
                  <TableCell>{payment.stenography}</TableCell>
                  <TableCell>{payment.transcription}</TableCell>
                  <TableCell>{payment.pantry_service}</TableCell>
                  <TableCell>{payment.video_conference}</TableCell>
                  <TableCell>
                    <Button
                      onClick={() => handleEdit(payment)}
                      style={{
                        marginRight: "8px",
                        backgroundColor: "#083266",
                        color: "#fff",
                      }}
                      disabled={loading}
                    >
                      Edit
                    </Button>
                    {/* <Button
                      onClick={() => handleDelete(payment.id)}
                      style={{
                        marginRight: "8px",
                        backgroundColor: "#083266",
                        color: "#fff",
                      }}
                      disabled={loading}
                    >
                      Delete
                    </Button> */}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div style={{ padding: "16px", textAlign: "center" }}>
            No venue payments found
          </div>
        )}
      </Paper>
    </div>
  );
};

export default withRouter(VenuePayments);